/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SideBar.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import {
  Box,
  Collapse,
  CssBaseline,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MuiDrawer from "@mui/material/Drawer";
import { sideBarData } from "./sideBarData";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import profileActions from "../../redux/actions/profile";

function SideBar() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Only subscribe to the specific fields needed for menu rendering
  const roleId = useSelector((state) => state.profileReducer?.profile?.role_id);
  const userPageAccess = useSelector(
    (state) => state.profileReducer?.profile?.user_page_access,
  );
  const roleAccessPages = useSelector(
    (state) => state.profileReducer?.profile?.role_details?.access_to_pages,
  );

  const [menuOptions, setMenuOptions] = React.useState([]);
  const [expandedItems, setExpandedItems] = React.useState({});
  const menuInitializedRef = React.useRef(false);

  React.useEffect(() => {
    dispatch(HandleApiActions({ ...profileActions.get_profile_details }));
  }, []);

  React.useEffect(() => {
    if (roleId && !menuInitializedRef.current) {
      if (roleId == 1000) {
        setMenuOptions(() => roleAccessPages);
      } else {
        setMenuOptions(() =>
          userPageAccess?.length ? userPageAccess : roleAccessPages,
        );
      }
      menuInitializedRef.current = true;
    }
  }, [roleId, userPageAccess, roleAccessPages]);

  //1.useState
  const [open, setOpen] = React.useState(true);

  //3.Funtions
  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleClickToPage = (to) => {
    navigate(to);
    // setOpen(!open);
  };

  const handleExpandClick = (itemTitle) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemTitle]: !prev[itemTitle],
    }));
  };


  return (
    <div>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100%",
          boxShadow: "3",
        }}
      >
        <CssBaseline />
        {!isMobile && (
          <Drawer variant="permanent" open={open} sx={{ position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                px: 1.5,
                py: 1.5,
                backgroundColor: "black",
                minHeight: "32px",
                mb: ".3rem",
              }}
            >
              <IconButton
                size="small"
                sx={{
                  color: "white",
                  p: 0,
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mr: 0.4,
                }}
                onClick={toggleDrawer}
              >
                {open ? (
                  <ArrowBackIosNewOutlinedIcon sx={{ fontSize: "1.2rem" }} />
                ) : (
                  <MenuIcon sx={{ fontSize: "1.4rem" }} />
                )}
              </IconButton>
            </Box>
            <Box
              sx={{
                backgroundColor: "black",
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "calc(100vh - 100px)",

                // Custom scrollbar for WebKit-based browsers
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "black", // background of the scrollbar
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888", // the scrollbar handle
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#555",
                },

                // Firefox support
                scrollbarColor: "#888 black", // thumb color and track color
                scrollbarWidth: "thin",
              }}
            >
              <List
                component="nav"
                sx={{
                  width: "100%",
                  marginLeft: open ? "0px" : "5px",
                  marginRight: open ? "0px" : "-5px",
                  padding: open ? "0px" : "0px",
                }}
              >
                {sideBarData?.map((data, index) => {
                  const isAllowed = true;
                  if (isAllowed) {
                    const hasSubItems =
                      data.subItems && data.subItems.length > 0;
                    const isExpanded = expandedItems[data.title];

                    // Check if this main menu item's path matches the current route
                    const isActive = window.location.pathname.startsWith(
                      data.path,
                    );

                    // Check if any submenu item is active
                    const isSubItemActive =
                      hasSubItems &&
                      data.subItems.some((subItem) =>
                        window.location.pathname.startsWith(subItem.path),
                      );

                    // Unified active styling for main items
                    const isMenuActive = isActive || isSubItemActive;
                    const mainBgColor = isMenuActive
                      ? "#FFFFFF"
                      : "transparent";
                    const mainTextColor = isMenuActive ? "#000000" : "#E0E0E0";

                    return (
                      <Box key={index} sx={{ mb: 0 }}>
                        <ListItemButton
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: mainBgColor,
                            color: mainTextColor,
                            // borderRadius: "8px",
                            // mb: ".3rem",
                            width: "238px",
                            height: "32px",
                            "&:hover": {
                              backgroundColor: "#9AEB75",
                              color: "#FFFFFF",
                              "& .MuiListItemIcon-root": {
                                color: "#FFFFFF !important",
                                "& img": {
                                  filter: "brightness(0) invert(1) !important",
                                },
                                "& svg": {
                                  color: "#FFFFFF !important",
                                },
                              },
                              "& .MuiListItemText-root": {
                                color: "#FFFFFF !important",
                                "& .MuiTypography-root": {
                                  color: "#FFFFFF !important",
                                },
                              },
                              "& .expand-collapse-icon": {
                                color: "#FFFFFF !important",
                              },
                            },
                            px: 1.5,
                            py: 1.5,
                            minHeight: 32,
                            pr: 1,
                          }}
                          onClick={() => {
                            if (hasSubItems) {
                              // If sidebar is closed, open it first
                              if (!open) {
                                setOpen(true);
                              }
                              // Then expand the submenu
                              handleExpandClick(data.title);
                            } else {
                              handleClickToPage(data.path);
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: mainTextColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 40,
                                "& svg": {
                                  width: "18px",
                                  height: "20px",
                                  color: `${mainTextColor} !important`,
                                },
                                "& img": {
                                  width: "18px",
                                  height: "20px",
                                  // Active (main or submenu): make image icons black; otherwise white
                                  filter: isMenuActive
                                    ? "brightness(0)"
                                    : "brightness(0) invert(1)",
                                },
                              }}
                            >
                              {data.icon}
                            </ListItemIcon>
                            {open && (
                              <ListItemText
                                primary={
                                  data.title === "Type Approvals"
                                    ? "Regulatory Compliance"
                                    : data.title
                                }
                                sx={{
                                  color: mainTextColor,
                                  "& .MuiTypography-root": {
                                    // fontFamily: "Open Sans",
                                    fontStyle: "Open Sans",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    lineHeight: "20px",
                                    letterSpacing: "0%",
                                  },
                                }}
                              />
                            )}
                          </Box>
                          {open && hasSubItems && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "28px",
                                minWidth: "28px",
                                ml: "auto",
                              }}
                            >
                              {isExpanded ? (
                                <ExpandLess
                                  className="expand-collapse-icon"
                                  sx={{
                                    color: `${mainTextColor} !important`,
                                    fontSize: "18px !important",
                                    cursor: "pointer",
                                  }}
                                />
                              ) : (
                                <ExpandMore
                                  className="expand-collapse-icon"
                                  sx={{
                                    color: `${mainTextColor} !important`,
                                    fontSize: "18px !important",
                                    cursor: "pointer",
                                  }}
                                />
                              )}
                            </Box>
                          )}
                          {open && !hasSubItems && (
                            <Box
                              sx={{
                                width: "28px",
                                minWidth: "28px",
                                ml: "auto",
                              }}
                            />
                          )}
                        </ListItemButton>

                        {/* Sub Items */}
                        {hasSubItems && open && (
                          <Collapse
                            in={isExpanded || isSubItemActive}
                            timeout="auto"
                            unmountOnExit
                          >
                            <List component="div" disablePadding sx={{ mt: 0 }}>
                              {data.subItems.map((subItem, subIndex) => {
                                const isSubMenuActive =
                                  window.location.pathname.startsWith(
                                    subItem.path,
                                  );
                                return (
                                  <ListItemButton
                                    key={subIndex}
                                    sx={{
                                      pl: 7,
                                      py: 1,
                                      backgroundColor: isSubMenuActive
                                        ? "#FFFFFF"
                                        : "transparent",
                                      color: isSubMenuActive
                                        ? "#000000"
                                        : "#FFFFFF",
                                      width: "235px",
                                      height: "32px",
                                      "&:hover": {
                                        backgroundColor: "#9AEB75",
                                        color: "#FFFFFF",
                                        "& .MuiTypography-root": {
                                          color: "#FFFFFF !important",
                                        },
                                      },
                                    }}
                                    onClick={() =>
                                      handleClickToPage(subItem.path)
                                    }
                                  >
                                    <ListItemText
                                      primary={subItem.title}
                                      sx={{
                                        "& .MuiTypography-root": {
                                          fontStyle: "Open Sans",
                                          fontSize: "14px",
                                          fontWeight: 400,
                                          lineHeight: "20px",
                                          letterSpacing: "0%",
                                          color: isSubMenuActive
                                            ? "#000000"
                                            : "#FFFFFF",
                                        },
                                      }}
                                    />
                                  </ListItemButton>
                                );
                              })}
                            </List>
                          </Collapse>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })}
              </List>
            </Box>

            {open && (
              <Box sx={{ backgroundColor: "black" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontStyle: "Open Sans",
                    mt: "10px",
                    fontWeight: { xs: 300, sm: 400, md: "bold" },
                    textAlign: "center",
                    color: "white",
                    fontSize: { xs: "0.7rem", sm: "0.85rem", md: "1rem" },
                  }}
                >
                  Version: {process.env.REACT_APP_VERSION}
                </Typography>
              </Box>
            )}
          </Drawer>
        )}
      </Box>
    </div>
  );
}

export default SideBar;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: 235, // default width for md+
    [theme.breakpoints.down("md")]: {
      width: 200,
    },
    [theme.breakpoints.down("sm")]: {
      width: 80,
    },
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: "black",
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(8),
    }),
  },
}));
